<script setup lang="ts">
import { apps, formatting, Models, useContext, useEnums, useOrderViewContext } from "@hantera/portal-app";
import { ContextMenuSection, ContextMenuSections, EnumsBar, Expandable, Panel } from "@hantera/design-system";
import { computed, ref } from "vue";
import useGraphPhrases from "@/composition/use-graph-phrases";
import InvoiceCapture from "@/components/Order/Payments/InvoiceCapture.vue";
import useApps from "@/composition/use-apps";
import AppSlotView from "@/AppSlotView.vue";

const props = defineProps<{
  payment: Models.Graph.Payment
}>()

const orderViewContext = useOrderViewContext();
const portalContext = useContext();

const graphPhrases = useGraphPhrases();
const appsContext = useApps()
const enums = useEnums()


const expanded = ref(true);

const sortedJournalEntries = computed(() => {
  return [...props.payment.journalEntries].sort((a, b) => a.paymentJournalEntryNumber.sub(b.paymentJournalEntryNumber).toNumber());
})

function debit(entry: Models.Graph.PaymentJournalEntry) {
  if (entry.paymentJournalEntryType === 'charge') {
    if (entry.amount.gt(0)) {
      return formatting.currency(entry.amount, orderViewContext.state.currencyCode)
    }
  }
  if (entry.paymentJournalEntryType === 'capture') {
    if (entry.amount.lt(0)) {
      return formatting.currency(-entry.amount, orderViewContext.state.currencyCode)
    }
  }
}

function credit(entry: Models.Graph.PaymentJournalEntry) {
  if (entry.paymentJournalEntryType === 'charge') {
    if (entry.amount.lt(0)) {
      return formatting.currency(-entry.amount, orderViewContext.state.currencyCode)
    }
  }
  if (entry.paymentJournalEntryType === 'capture') {
    if (entry.amount.gt(0)) {
      return formatting.currency(entry.amount, orderViewContext.state.currencyCode)
    }
  }
}

const availableTags = computed(() => {
  return enums.valueSets('payment', 'tags').value;
})

const paymentReferenceContextMenu = computed(() => {
  return <ContextMenuSections>[[{
    label: `Copy ${props.payment.externalReference ?? props.payment.paymentNumber}`,
    action() {
      navigator.clipboard.writeText(props.payment.externalReference ?? props.payment.paymentNumber)
    }
  }]]
})

const paymentContextMenu = computed(() => {
  const result: ContextMenuSections = []

  for (const provider of appsContext.getServices(apps.standardServices.orderView.contextMenuProvider)) {
    const section = provider.buildPaymentContextMenu?.(portalContext, orderViewContext, props.payment)
    if (section && section.length > 0) {
      result.push(section)
    }
  }


  return result
})
</script>


<template>
  <Panel class="payment light:box-pop" v-context-menu="paymentContextMenu">
    <Expandable v-model="expanded">

      <template v-slot:label>
        <div class="payment-header">
          
          <div class="payment__header-details">
            <div class="payment__title">
              <div
                class="payment__number"
                v-context-menu="paymentReferenceContextMenu"
              >{{ payment.externalReference ?? payment.paymentNumber }}</div>
              <div
                class="payment__provider"
                v-if="payment.providerKey"
              >{{ enums.value('payment', 'providerKey', payment.providerKey).value?.label }}</div>
              
              <AppSlotView :slot="apps.orderViewSlots.payment.header" :context="{
                payment: payment
              }" />
            </div>
            <div class="payment__tags">
              <EnumsBar
                :available-sets="availableTags"
                :selected-values="payment.tags"
              />
            </div>
          </div>
        </div>
      </template>
      <template v-slot:default>
        
        <div class="payment-details">
          <div class="box-content p payment-summary">
            <div
              :class="{
                'payment-summary--highlight': !payment.authorizedAmount.eq(0)
              }"
            >
              <div>Authorized</div>
              <div>{{ formatting.currency(payment.authorizedAmount, payment.currencyCode) }}</div>
            </div>
            <div
              :class="{
                'payment-summary--highlight': !payment.chargedBalance.eq(0)
              }"
            >
              <div>Charged Balance</div>
              <div>{{ formatting.currency(payment.chargedBalance, payment.currencyCode) }}</div>
            </div>
            <div
              :class="{
                'payment-summary--highlight': !payment.capturedBalance.eq(0)
              }"
            >
              <div>Captured Balance</div>
              <div>{{ formatting.currency(payment.capturedBalance, payment.currencyCode) }}</div>
            </div>
            <div
              :class="{
                'payment-summary--highlight': !payment.creditBalance.eq(0)
              }"
            >
              <div>Credit Balance</div>
              <div
                :class="{
                  'text--error': payment.creditBalance.lt(0)
                }"
              >
                {{ formatting.currency(payment.creditBalance, payment.currencyCode) }}
              </div>
            </div>
          </div>

          <div class="payment-app-details" v-if="appsContext.getComponentsFor(apps.orderViewSlots.payment.details).length > 0">
            <AppSlotView :slot="apps.orderViewSlots.payment.details" :context="{
              payment: payment
            }" />
          </div>

          <div class="payment-captures" v-if="payment.captures.length > 0">
            <div class="box-content-label">
              Invoices
            </div>
            <div class="payment-invoices">
              <InvoiceCapture
                v-for="capture in payment.captures"
                :key="capture.paymentCaptureId"
                :capture="capture"
              />
            </div>
          </div>

          
          <div class="payment-journal">

            <div class="payment-journal-header">
              <div class="payment-journal__cell">
                #
              </div>
              <div class="payment-journal__cell">
                Timestamp
              </div>
              <div class="payment-journal__cell">
                Type
              </div>
              <div class="payment-journal__cell">
                Invoice
              </div>
              <div class="payment-journal__cell">
                Reference
              </div>
              <div class="payment-journal__cell">
                Debit
              </div>
              <div class="payment-journal__cell">
                Credit
              </div>
            </div>
            
            <div class="box-content p payment-journal-entries">
  
              <div
                v-for="entry in sortedJournalEntries"
                :key="entry.paymentJournalEntryId"
                class="payment-journal-entry"
              >
                <div class="payment-journal__cell">{{ formatting.number(entry.paymentJournalEntryNumber) }}</div>
                <div class="payment-journal__cell">{{ formatting.dateTime(entry.timestamp) }}</div>
                <div class="payment-journal__cell">{{ entry.paymentJournalEntryType }}</div>
                <div class="payment-journal__cell">{{ entry.invoice?.invoiceNumber }}</div>
                <div class="payment-journal__cell">{{ entry.transactionReference }}</div>
                <div class="payment-journal__cell">{{ debit(entry) }}</div>
                <div class="payment-journal__cell">{{ credit(entry) }}</div>
              </div>
            </div>
          </div>

          <div class="payment-footer" v-if="appsContext.getComponentsFor(apps.orderViewSlots.payment.footer).length > 0">
            <AppSlotView :slot="apps.orderViewSlots.payment.footer" :context="{
              payment: payment
            }" />
          </div>

        </div>
      </template>

    </Expandable>
  </Panel>
</template>

<style scoped>
h3 {
  margin: 5px 0;
}

.payment {
  max-width: 800px;
}

.payment :deep(.expandable__label) {
  padding: 0;
}

.payment-header {
  position: relative;
  padding-left: 10px;
  display: flex;
  align-items: center;
}

.payment__title {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  gap: 10px;
  align-items: center;
}

.payment__number {
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  white-space: nowrap;
}

.payment-summary {
  display: grid;
  grid-template-columns: auto;
  grid-auto-flow: column;
  text-align: right;
}

.payment-summary--highlight {
  font-weight: bold;
}

.payment-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px;
}

.payment-summary {
  grid-column: 1 / -1;
}

.payment-app-details,
.payment-footer {
  grid-column: 1 / -1;
}

.payment-fields {
  grid-column: 1 / 2;
}

.payment-captures {
  grid-column: 2 / 3;
}

.payment-invoices {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.payment-journal {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: max-content max-content max-content max-content auto max-content max-content;
  column-gap: 10px;
  padding-top: 5px;
}

.payment-journal-header {
  display: contents;
  font-size: 12px;
}

.payment-journal-entries {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
}

.payment-journal-entry {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
}

.payment-journal-entry:hover {
  background-color: hsl(var(--color-80));
}

.payment-journal__cell:nth-last-child(2) {
  text-align: right;
}

.payment-journal__cell:last-child {
  text-align: right;
}
</style>